import { useEffect, useState } from "react";
import PageShell from "@/components/site/PageShell";
import SEO from "@/components/site/SEO";
import { Download, ExternalLink, Mail, Phone } from "lucide-react";

const DPO = {
  name: "Ivanice Tressoldi",
  email: "dpo@licitoguru.com",
  phone: "(49) 99105-0445",
  telHref: "tel:5549991050445",
};

const DOCS = [
  {
    id: "aviso-privacidade",
    title: "Aviso de Privacidade",
    desc: "Como tratamos, protegemos e compartilhamos seus dados pessoais na plataforma licito.guru.",
    href:
      "https://customer-assets-v7afamib.emergentagent.net/job_guru-landing-v2/artifacts/35f3utmj_AVISO%20DE%20PRIVACIDADE%20DA%20PLATAFORMA%20LICITO.docx%20%281%29.pdf",
    file: "aviso-de-privacidade-licito.guru.pdf",
  },
  {
    id: "termos",
    title: "Termos de Uso",
    desc: "Condições gerais de acesso, navegação e uso da plataforma.",
    href:
      "https://customer-assets-v7afamib.emergentagent.net/job_guru-landing-v2/artifacts/v5duwj5c_termos-de-uso.pdf",
    file: "termos-de-uso.pdf",
  },
  {
    id: "cookies",
    title: "Política de Cookies",
    desc: "Uso de cookies e tecnologias semelhantes no site e na plataforma.",
    href:
      "https://customer-assets-v7afamib.emergentagent.net/job_guru-landing-v2/artifacts/kobn67py_politica-de-cookies.pdf",
    file: "politica-de-cookies.pdf",
  },
];

const EXTERNAL_LAWS = [
  {
    n: "01",
    label: "LGPD, Lei nº 13.709/2018",
    href:
      "https://www.planalto.gov.br/ccivil_03/_ato2015-2018/2018/lei/l13709.htm",
  },
  {
    n: "02",
    label:
      "Normas e orientações emitidas pela Autoridade Nacional de Proteção de Dados (ANPD)",
    href: "https://www.gov.br/anpd/pt-br",
  },
];

const DPO_FUNCTIONS = [
  "I. Receber reclamações e comunicações dos titulares, prestar esclarecimentos e adotar providências.",
  "II. Orientar os colaboradores e os contratados no cumprimento das práticas necessárias à privacidade de dados pessoais.",
  "III. Quando provocado, entregar o Relatório de Impacto de Proteção aos Dados Pessoais, na forma da lei, com o apoio técnico das áreas jurídica e tecnológica da entidade.",
  "IV. Atuar como canal de comunicação entre o controlador, os titulares dos dados e a Autoridade Nacional de Proteção de Dados (ANPD), cumprindo com atribuições que possam vir a ser estabelecidas pela ANPD.",
  "V. Informar, quando obrigatório, à Autoridade Nacional de Proteção de Dados (ANPD) a comunicação ou o uso compartilhado de dados pessoais de pessoas naturais ou jurídicas de direito privado.",
  "VI. Atender às normas complementares da Autoridade Nacional de Proteção de Dados (ANPD).",
  "VII. Informar à Autoridade Nacional de Proteção de Dados (ANPD) e aos titulares dos dados pessoais eventuais incidentes de privacidade de dados pessoais, dentro da execução de um plano de respostas a incidentes.",
  "VIII. Elaborar e acompanhar a execução do Plano de Adequação à Lei Geral de Proteção de Dados.",
  "IX. Realizar e manter atualizado o mapeamento, inventário e análise de riscos do tratamento dos dados pessoais.",
  "X. Aprovar, prover condições e promover ações para efetividade do Plano de Adequação de Proteção de Dados junto à ANPD.",
  "XI. Elaborar o Relatório de Impacto de Proteção aos Dados Pessoais, com o apoio técnico da área de tecnologia da empresa, quando necessário.",
  "XII. Fornecer aos operadores termos de uso, manuais de instruções e treinamento.",
  "XIII. Monitorar e supervisionar o cumprimento da Lei Federal nº 13.709/2018 na empresa.",
  "XIV. Sugerir políticas, fluxos, providências e propostas de regulamentação a serem adotadas com vistas à implantação das diretrizes previstas na Lei Federal nº 13.709/2018.",
  "XV. Auxiliar, conscientizar e divulgar a Lei Federal nº 13.709/2018 junto aos colaboradores, visando estimular a mudança de cultura necessária para adequação à norma.",
  "XVI. Aprovar a comunicação de incidentes à Autoridade Nacional de Proteção de Dados Pessoais e aos titulares dos dados pessoais pelo encarregado.",
  "XVII. Constituir Comitê de proteção de dados especial, quando necessário.",
];

const TITULAR_RIGHTS = [
  "I. Confirme se seus dados pessoais estão sendo processados e acessar as informações sobre o tratamento desses dados.",
  "II. Realize a correção de dados pessoais incompletos, inexatos ou desatualizados.",
  "III. Anonimize, bloqueie ou elimine os dados pessoais desnecessários, excessivos ou tratados em desconformidade com a Lei Federal nº 13.709/2018.",
  "IV. Receba seus dados pessoais em formato estruturado, interoperável e de uso comum, facilitando a portabilidade para outro fornecedor de serviço.",
  "V. Elimine os dados pessoais tratados com base no seu consentimento, exceto em situações em que a legislação exige a retenção desses dados.",
  "VI. Forneça acesso à informação sobre com quem os dados pessoais foram compartilhados e sua base legal.",
  "VII. Realize a revisão de decisões tomadas unicamente com base em processamento automatizado de dados pessoais que afetem seus interesses.",
];

const TOC = [
  { id: "marco-legal", label: "Marco legal" },
  { id: "lgpd", label: "LGPD 13.709/2018" },
  { id: "encarregado", label: "Encarregado de Dados" },
  { id: "direitos", label: "Direitos do Titular" },
  { id: "documentos", label: "Legislação e Documentos" },
  { id: "implementacao", label: "Implementação da LGPD" },
];

export default function LGPD() {
  const [activeId, setActiveId] = useState(TOC[0].id);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    TOC.forEach((t) => {
      const el = document.getElementById(t.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="LGPD e Proteção de Dados | licito.guru"
        description="Política de tratamento de dados pessoais da licito.guru. Aviso de Privacidade, Termos de Uso, Política de Cookies e canal de atendimento da Encarregada de Dados (DPO)."
        path="/lgpd"
      />
    <PageShell
      kicker="LGPD 13.709/2018"
      title="Proteção de Dados Pessoais."
      lead="A licito.guru, em atendimento ao que determina a Lei Federal nº 13.709, de 14 de agosto de 2018, a Lei Geral de Proteção de Dados Pessoais (LGPD), apresenta sua página de Proteção de Dados Pessoais."
    >
      <p className="text-[13px] text-[#1A3A61]/50 mt-2 mb-14">
        Última atualização: 26 de junho de 2026
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Table of contents */}
        <aside className="lg:col-span-3 lg:sticky lg:top-28 self-start">
          <span className="kicker">Nesta página</span>
          <ul className="mt-4 space-y-1 border-l hairline">
            {TOC.map((t) => (
              <li key={t.id}>
                <a
                  href={`#${t.id}`}
                  data-testid={`lgpd-toc-${t.id}`}
                  className={`block pl-4 py-2 text-[14px] leading-snug transition-colors -ml-px border-l-2 ${
                    activeId === t.id
                      ? "border-[#1A3A61] text-[#1A3A61] font-medium"
                      : "border-transparent text-[#1A3A61]/60 hover:text-[#1A3A61]"
                  }`}
                >
                  {t.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        {/* Main content */}
        <div className="lg:col-span-9 space-y-16 md:space-y-20">
          {/* Marco legal */}
          <section id="marco-legal" className="scroll-mt-28">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 items-start border-t hairline pt-8">
              <div className="sm:col-span-1">
                <span className="kicker">Marco legal</span>
                <p className="mt-3 font-display text-[#1A3A61] text-5xl md:text-6xl leading-none tracking-[-0.02em]">
                  2018
                </p>
              </div>
              <p className="sm:col-span-3 text-[#1A3A61]/75 text-[16px] leading-[1.65]">
                A LGPD protege os direitos fundamentais de liberdade e privacidade e
                orienta o tratamento adequado de dados pessoais.
              </p>
            </div>
          </section>

          {/* LGPD 13.709/2018 */}
          <section id="lgpd" className="scroll-mt-28">
            <span className="kicker">LGPD</span>
            <h2 className="mt-3 font-display text-[#1A3A61] text-3xl md:text-4xl leading-[1.05] tracking-[-0.015em]">
              Lei Geral de Proteção de Dados Pessoais (LGPD) 13.709/2018
            </h2>
            <div className="mt-6 space-y-5 text-[#1A3A61]/80 text-[16px] leading-[1.7]">
              <p>
                A Lei Geral de Proteção de Dados (LGPD) dispõe sobre o tratamento de
                dados pessoais, por pessoa natural ou por pessoa jurídica de direito
                público ou privado, com o objetivo de proteger os direitos
                fundamentais de liberdade e de privacidade e o livre desenvolvimento
                da personalidade da pessoa natural. Dados pessoais são as informações
                relacionadas a pessoa natural identificada ou identificável.
              </p>
              <p>
                Antes de iniciar qualquer tipo de tratamento de dados pessoais, isto
                é, qualquer atividade que utiliza um dado pessoal, o agente de
                tratamento deve certificar-se de que a finalidade da operação esteja
                registrada de forma clara e explícita, e de que os propósitos sejam
                especificados e informados ao titular dos dados. A principal
                finalidade do tratamento pela licito.guru está relacionada à execução
                de contrato e ao legítimo interesse do controlador, devidamente
                previstos em lei, regulamentos ou respaldados em contratos.
              </p>
              <p>
                A LGPD tem como objetivo garantir o tratamento adequado de dados
                pessoais, primando pela segurança e proteção de dados, a proteção aos
                direitos fundamentais de liberdade e de privacidade e o livre
                desenvolvimento da personalidade da pessoa natural.
              </p>
              <p>
                O compartilhamento de dados pessoais dentro da finalidade da
                licito.guru está previsto na lei e dispensa o consentimento
                específico.
              </p>
              <p>
                A licito.guru observa as hipóteses legais e os princípios da LGPD
                para assegurar a conformidade do tratamento de dados pessoais.
                Foram elaborados o Aviso de Privacidade, os Termos de Uso e a
                Política de Cookies. Estão em fase de elaboração a política de
                resposta a incidentes de dados e a Cartilha de Proteção de Dados
                Pessoais.
              </p>
            </div>
          </section>

          {/* Encarregado de Dados */}
          <section id="encarregado" className="scroll-mt-28">
            <span className="kicker">LGPD</span>
            <h2 className="mt-3 font-display text-[#1A3A61] text-3xl md:text-4xl leading-[1.05] tracking-[-0.015em]">
              Encarregado de Dados
            </h2>
            <p className="mt-6 text-[#1A3A61]/80 text-[16px] leading-[1.7]">
              A gestão da matéria de proteção de dados pessoais pela licito.guru é
              desenvolvida pelo Encarregado de Dados, cujas funções são:
            </p>
            <ol className="mt-6 space-y-3">
              {DPO_FUNCTIONS.map((f, i) => (
                <li
                  key={i}
                  className="flex gap-4 text-[#1A3A61]/85 text-[15.5px] leading-[1.6] border-b hairline pb-3 last:border-b-0"
                >
                  <span className="text-[11px] font-mono text-[#1A3A61]/40 mt-1 flex-shrink-0 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ol>

            {/* DPO contact card */}
            <div className="mt-10 p-6 md:p-7 border hairline rounded-md bg-[#F7F8FA]">
              <span className="kicker">Canal de atendimento da Encarregada de Dados (DPO)</span>
              <p className="mt-3 text-[#1A3A61]/80 text-[15.5px] leading-[1.65]">
                Caso não tenha encontrado as informações que buscava ou deseje
                esclarecer dúvidas sobre o tratamento dos dados pessoais pela
                LicitoGuru, entre em contato com a Encarregada de Dados (DPO) da
                Licito.Guru, {DPO.name}, advogada inscrita na OAB/SC 50.565, por
                meio do endereço de e-mail {DPO.email} e telefone {DPO.phone}.
              </p>
              <div className="mt-5 flex flex-wrap gap-4">
                <a
                  href={`mailto:${DPO.email}`}
                  data-testid="lgpd-dpo-email"
                  className="inline-flex items-center gap-2 text-[15px] text-[#1A3A61] font-medium hover:text-[#00C2D1]"
                >
                  <Mail size={16} /> {DPO.email}
                </a>
                <a
                  href={DPO.telHref}
                  data-testid="lgpd-dpo-phone"
                  className="inline-flex items-center gap-2 text-[15px] text-[#1A3A61] font-medium hover:text-[#00C2D1]"
                >
                  <Phone size={16} /> {DPO.phone}
                </a>
              </div>
            </div>
          </section>

          {/* Direitos do Titular */}
          <section id="direitos" className="scroll-mt-28">
            <span className="kicker">LGPD</span>
            <h2 className="mt-3 font-display text-[#1A3A61] text-3xl md:text-4xl leading-[1.05] tracking-[-0.015em]">
              Direitos do Titular
            </h2>
            <p className="mt-6 text-[#1A3A61]/80 text-[16px] leading-[1.7]">
              A LGPD assegura a toda pessoa natural a titularidade de seus dados
              pessoais, garantidos os direitos fundamentais de liberdade, de
              intimidade e de privacidade. Para que o titular possa executar os seus
              direitos, poderá solicitar que a licito.guru:
            </p>
            <ol className="mt-6 space-y-3">
              {TITULAR_RIGHTS.map((r, i) => (
                <li
                  key={i}
                  className="flex gap-4 text-[#1A3A61]/85 text-[15.5px] leading-[1.6] border-b hairline pb-3 last:border-b-0"
                >
                  <span className="text-[11px] font-mono text-[#1A3A61]/40 mt-1 flex-shrink-0 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{r}</span>
                </li>
              ))}
            </ol>
            <p className="mt-6 text-[#1A3A61]/75 text-[15px] leading-[1.65]">
              Os direitos do titular podem ser exercidos através de solicitação ao
              DPO, nos contatos acima.
            </p>
            <p className="mt-2 text-[#1A3A61]/75 text-[15px] leading-[1.65]">
              Quando do exercício dos direitos pelo titular, poderão ser solicitados
              documento e informações para confirmação de sua identidade.
            </p>
          </section>

          {/* Documentos */}
          <section id="documentos" className="scroll-mt-28">
            <span className="kicker">LGPD</span>
            <h2 className="mt-3 font-display text-[#1A3A61] text-3xl md:text-4xl leading-[1.05] tracking-[-0.015em]">
              Legislação e Documentos
            </h2>
            <p className="mt-6 text-[#1A3A61]/80 text-[16px] leading-[1.7]">
              Mais informações podem ser obtidas na legislação aplicável:
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {EXTERNAL_LAWS.map((l) => (
                <a
                  key={l.n}
                  data-testid={`lgpd-law-${l.n}`}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-4 border hairline rounded-md p-5 hover:border-[#1A3A61] transition-colors"
                >
                  <span className="text-[11px] font-mono text-[#1A3A61]/40 mt-1 flex-shrink-0">
                    {l.n}
                  </span>
                  <span className="flex-1 text-[#1A3A61] text-[15px] font-medium leading-snug">
                    {l.label}
                  </span>
                  <ExternalLink
                    size={16}
                    className="mt-1 text-[#1A3A61]/40 group-hover:text-[#1A3A61] flex-shrink-0"
                  />
                </a>
              ))}
            </div>

            <div className="mt-10">
              <p className="text-[#1A3A61] font-medium text-[16px]">
                Documentos da licito.guru
              </p>
              <p className="mt-2 text-[#1A3A61]/70 text-[14px] leading-relaxed">
                Documentos oficiais sobre o uso da plataforma e o tratamento de dados
                pessoais. Todos abrem em uma nova aba, em formato PDF.
              </p>

              <ul className="mt-5 divide-y hairline border-y hairline">
                {DOCS.map((d) => (
                  <li key={d.id}>
                    <a
                      data-testid={`lgpd-doc-${d.id}`}
                      href={d.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={d.file}
                      className="group flex items-center gap-4 py-4 md:py-5"
                    >
                      <span className="flex-shrink-0 h-10 w-10 rounded-md bg-[#F1F5FF] flex items-center justify-center text-[#1A3A61] group-hover:bg-[#1A3A61] group-hover:text-white transition-colors">
                        <Download size={16} />
                      </span>
                      <span className="flex-1">
                        <span className="block text-[#1A3A61] text-[15.5px] font-medium leading-tight group-hover:text-[#00C2D1]">
                          {d.title}
                        </span>
                        <span className="block text-[#1A3A61]/60 text-[13.5px] mt-1">
                          {d.desc}
                        </span>
                      </span>
                      <span className="hidden sm:inline text-[11px] font-mono tracking-wide uppercase text-[#1A3A61]/40 group-hover:text-[#1A3A61]">
                        PDF · Baixar
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Implementação */}
          <section id="implementacao" className="scroll-mt-28">
            <span className="kicker">LGPD</span>
            <h2 className="mt-3 font-display text-[#1A3A61] text-3xl md:text-4xl leading-[1.05] tracking-[-0.015em]">
              Implementação da LGPD na licito.guru
            </h2>
            <div className="mt-6 space-y-5 text-[#1A3A61]/80 text-[16px] leading-[1.7]">
              <p>
                A licito.guru atua continuamente para adequar as suas atividades à
                LGPD e às orientações da Autoridade Nacional de Proteção de Dados
                (ANPD), com atenção aos dados pessoais coletados e aos fluxos
                de tratamento.
              </p>
            </div>
          </section>
        </div>
      </div>
    </PageShell>
    </>
  );
}
